import { useEffect, useRef, useState } from 'react';
import { toasterApi } from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import { useShallow } from 'zustand/shallow';
import {
  isForceUpdateRequired,
  isUpdatePendingState,
  REMIND_LATER_DURATION_MS,
} from '../store/autoUpdaterPreferences.logic';
import { useAutoUpdaterPreferencesStore } from '../store/autoUpdaterPreferencesStore';

type UpdateInfo = {
  version: string;
  releaseDate?: string;
  releaseNotes?: string;
};

type UpdateProgress = {
  percent: number;
  bytesPerSecond: number;
  transferred: number;
  total: number;
};

type UpdateState =
  | 'idle'
  | 'checking'
  | 'available'
  | 'not-available'
  | 'preparing-download'
  | 'downloading'
  | 'downloaded'
  | 'error';

export type UseAutoUpdaterReturn = {
  currentVersion: string | null;
  updateState: UpdateState;
  updateInfo: UpdateInfo | null;
  downloadProgress: UpdateProgress | null;
  error: string | null;
  isSnoozed: boolean;
  isSkippedVersion: boolean;
  isDismissedForSession: boolean;
  isForceUpdate: boolean;
  hasPendingUpdate: boolean;
  shouldShowUpdateModal: boolean;
  checkForUpdates: () => Promise<void>;
  downloadUpdate: () => Promise<void>;
  installUpdate: () => Promise<void>;
  remindLater: () => void;
  snooze: ({ durationMs }: { durationMs: number }) => void;
  skipVersion: () => void;
  dismissForSession: () => void;
  resetSessionDismiss: () => void;
  clearSnooze: () => void;
};

export function useAutoUpdater(): UseAutoUpdaterReturn {
  const t = useTranslations('autoUpdater');
  const autoUpdater =
    typeof window !== 'undefined' ? window.electronAPI?.autoUpdater : undefined;
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [updateState, setUpdateState] = useState<UpdateState>('idle');
  const [updateInfo, setUpdateInfo] = useState<UpdateInfo | null>(null);
  const [downloadProgress, setDownloadProgress] =
    useState<UpdateProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const {
    snoozedUntil,
    skippedVersion,
    count,
    dialogOpenedByUser,
    setSnoozedUntil,
    setSkippedVersion,
    incrementRemindLater,
    setDialogOpenedByUser,
  } = useAutoUpdaterPreferencesStore(
    useShallow((state) => ({
      snoozedUntil: state.snoozedUntil,
      skippedVersion: state.skippedVersion,
      count: state.count,
      dialogOpenedByUser: state.dialogOpenedByUser,
      setSnoozedUntil: state.setSnoozedUntil,
      setSkippedVersion: state.setSkippedVersion,
      incrementRemindLater: state.incrementRemindLater,
      setDialogOpenedByUser: state.setDialogOpenedByUser,
    })),
  );
  const [isDismissedForSession, setIsDismissedForSession] = useState(false);
  const downloadUpdateRef = useRef<() => Promise<void>>(async () => undefined);
  const isDownloadInFlightRef = useRef(false);
  const isSnoozed = Boolean(snoozedUntil && snoozedUntil > Date.now());

  useEffect(() => {
    if (!snoozedUntil) {
      return;
    }

    if (snoozedUntil <= Date.now()) {
      setSnoozedUntil({ snoozedUntil: null });

      return;
    }

    const timeoutId = window.setTimeout(() => {
      setSnoozedUntil({ snoozedUntil: null });
    }, snoozedUntil - Date.now());

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [setSnoozedUntil, snoozedUntil]);

  useEffect(() => {
    const fetchCurrentVersion = async () => {
      if (!autoUpdater) return;

      try {
        const result = await autoUpdater.getCurrentVersion();

        if (result.success && result.data) {
          setCurrentVersion(result.data.version);
        }
      } catch (err) {
        console.error('Failed to get current version:', err);
      }
    };

    fetchCurrentVersion();
  }, [autoUpdater]);

  useEffect(() => {
    if (!autoUpdater) return;

    const unsubscribeChecking = autoUpdater.onUpdateChecking((data) => {
      if (data?.source !== 'manual') {
        return;
      }

      setUpdateState('checking');
      setError(null);
      setUpdateInfo(null);
      setDownloadProgress(null);
    });

    const unsubscribeAvailable = autoUpdater.onUpdateAvailable(
      (info: {
        version: string;
        releaseDate?: string;
        releaseNotes?: string;
        source?: 'manual' | 'startup' | 'interval' | 'download-refresh';
      }) => {
        if (
          info.source === 'download-refresh' ||
          isDownloadInFlightRef.current
        ) {
          setUpdateInfo(info);

          return;
        }

        setUpdateState('available');
        setUpdateInfo(info);
        setError(null);
        setIsDismissedForSession(false);
        if (info.source === 'manual') {
          setSnoozedUntil({ snoozedUntil: null });
        }
      },
    );

    const unsubscribeNotAvailable = autoUpdater.onUpdateNotAvailable(
      (info: {
        version?: string;
        source?: 'manual' | 'startup' | 'interval' | 'download-refresh';
      }) => {
        setUpdateState('not-available');
        setUpdateInfo(info?.version ? { version: info.version } : null);
        setError(null);
        if (info.source === 'manual') {
          toasterApi.success({
            title: t('toastUpToDateTitle'),
            description: info.version
              ? t('toastUpToDateDescription', { version: info.version })
              : t('toastUpToDateDescriptionNoVersion'),
          });
        }
      },
    );

    const unsubscribeProgress = autoUpdater.onUpdateDownloadProgress(
      (progress: {
        percent: number;
        bytesPerSecond: number;
        transferred: number;
        total: number;
      }) => {
        setUpdateState('downloading');
        setDownloadProgress(progress);
        setError(null);
      },
    );

    const unsubscribeDownloaded = autoUpdater.onUpdateDownloaded(
      (info: { version: string; releaseDate?: string }) => {
        isDownloadInFlightRef.current = false;
        setUpdateState('downloaded');
        setUpdateInfo(info);
        setDownloadProgress(null);
        setError(null);
        setIsDismissedForSession(false);
      },
    );

    const unsubscribeError = autoUpdater.onUpdateError(
      (err: {
        error: string;
        source?: 'manual' | 'startup' | 'interval' | 'download-refresh';
      }) => {
        isDownloadInFlightRef.current = false;
        setUpdateState('error');
        setError(err.error);
        setDownloadProgress(null);
        if (err.source === 'manual') {
          toasterApi.error({
            title: t('toastUpdateFailedTitle'),
            description: t('toastUpdateFailedDescription'),
          });
        }
      },
    );

    return () => {
      unsubscribeChecking();
      unsubscribeAvailable();
      unsubscribeNotAvailable();
      unsubscribeProgress();
      unsubscribeDownloaded();
      unsubscribeError();
    };
  }, [autoUpdater, setSnoozedUntil, t]);

  const checkForUpdates = async (): Promise<void> => {
    if (!autoUpdater) {
      console.error('Auto-updater not available');

      return;
    }

    try {
      setError(null);
      const result = await autoUpdater.checkForUpdates({ source: 'manual' });

      if (!result.success && result.error) {
        console.error('Check for updates error:', result.error);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to check for updates';

      console.error('Check for updates error:', errorMessage);
      toasterApi.error({
        title: t('toastUpdateCheckFailedTitle'),
        description: t('toastUpdateCheckFailedDescription'),
      });
      setError(errorMessage);
      setUpdateState('error');
    }
  };

  const downloadUpdate = async (): Promise<void> => {
    if (!autoUpdater) {
      console.error('Auto-updater not available');

      return;
    }

    if (isDownloadInFlightRef.current) {
      return;
    }

    isDownloadInFlightRef.current = true;
    setError(null);
    setDownloadProgress(null);
    setUpdateState('preparing-download');

    try {
      const result = await autoUpdater.downloadUpdate();

      if (!result.success) {
        isDownloadInFlightRef.current = false;
        const errorMessage = result.error ?? 'Failed to download update';

        console.error('Download update error:', errorMessage);
        toasterApi.error({
          title: t('toastDownloadFailedTitle'),
          description: t('toastDownloadFailedDescription'),
        });
        setError(errorMessage);
        setUpdateState('error');
      }
    } catch (err) {
      isDownloadInFlightRef.current = false;
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to download update';

      console.error('Download update error:', errorMessage);
      toasterApi.error({
        title: t('toastDownloadFailedTitle'),
        description: t('toastDownloadFailedDescription'),
      });
      setError(errorMessage);
      setUpdateState('error');
    }
  };

  const installUpdate = async (): Promise<void> => {
    if (!autoUpdater) {
      console.error('Auto-updater not available');

      return;
    }

    try {
      setError(null);
      const result = await autoUpdater.installUpdate();

      if (!result.success) {
        const errorMessage = result.error ?? 'Failed to install update';

        console.error('Install update error:', errorMessage);
        toasterApi.error({
          title: t('toastInstallFailedTitle'),
          description: t('toastInstallFailedDescription'),
        });
        setError(errorMessage);
        setUpdateState('error');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to install update';

      console.error('Install update error:', errorMessage);
      toasterApi.error({
        title: t('toastInstallFailedTitle'),
        description: t('toastInstallFailedDescription'),
      });
      setError(errorMessage);
      setUpdateState('error');
    }
  };

  downloadUpdateRef.current = downloadUpdate;

  const snooze = ({ durationMs }: { durationMs: number }) => {
    const snoozeExpiresAt = Date.now() + durationMs;

    setSnoozedUntil({ snoozedUntil: snoozeExpiresAt });
    setIsDismissedForSession(false);
  };

  const remindLater = () => {
    if (!updateInfo?.version) {
      return;
    }

    if (!dialogOpenedByUser) {
      incrementRemindLater({ version: updateInfo.version });
    }

    setDialogOpenedByUser({ openedByUser: false });
    snooze({ durationMs: REMIND_LATER_DURATION_MS });
    if (updateState === 'available') {
      void downloadUpdate();
    }
  };

  const skipVersion = () => {
    if (!updateInfo?.version) {
      return;
    }

    setSkippedVersion({ skippedVersion: updateInfo.version });
    setIsDismissedForSession(false);
  };

  const dismissForSession = () => {
    setIsDismissedForSession(true);
  };

  const resetSessionDismiss = () => {
    setIsDismissedForSession(false);
  };

  const clearSnooze = () => {
    setSnoozedUntil({ snoozedUntil: null });
  };

  const isSkippedVersion = Boolean(
    skippedVersion &&
      updateInfo?.version &&
      skippedVersion === updateInfo.version,
  );
  const isForceUpdate = isForceUpdateRequired({
    version: updateInfo?.version,
    count,
  });
  const hasPendingUpdate =
    isUpdatePendingState(updateState) && !isSkippedVersion;
  const shouldShowUpdateModal =
    hasPendingUpdate && !isSnoozed && (isForceUpdate || !isDismissedForSession);

  return {
    currentVersion,
    updateState,
    updateInfo,
    downloadProgress,
    error,
    isSnoozed,
    isSkippedVersion,
    isDismissedForSession,
    isForceUpdate,
    hasPendingUpdate,
    shouldShowUpdateModal,
    checkForUpdates,
    downloadUpdate,
    installUpdate,
    remindLater,
    snooze,
    skipVersion,
    dismissForSession,
    resetSessionDismiss,
    clearSnooze,
  };
}
